import { cn } from "../../lib/utils";

// Live site URL base
const LIVE_SITE_BASE = import.meta.env.VITE_LIVE_SITE_BASE || 
  (import.meta.env.PROD ? ".imonsite.co.uk" : ".localhost:8790");

interface CheckoutResultBannerProps {
  result: "success" | "cancel";
  subdomain?: string | null;
  onDismiss: () => void;
}

export function CheckoutResultBanner({ result, subdomain, onDismiss }: CheckoutResultBannerProps) {
  const isSuccess = result === "success";

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-4 py-3 border-b",
        isSuccess
          ? "bg-emerald-500/10 border-emerald-500/20"
          : "bg-amber-500/10 border-amber-500/20"
      )}
    >
      <div className="flex items-center gap-3">
        {isSuccess ? (
          <>
            <div className="flex-shrink-0 size-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg
                className="size-5 text-emerald-600 dark:text-emerald-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Payment successful! Your site is now live 🎉
              </p>
              {subdomain && (
                <a
                  href={`https://${subdomain}${LIVE_SITE_BASE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-emerald-600 dark:text-emerald-400 underline hover:no-underline"
                >
                  Visit {subdomain}{LIVE_SITE_BASE} →
                </a>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex-shrink-0 size-8 rounded-full bg-amber-500/20 flex items-center justify-center">
              <svg
                className="size-5 text-amber-600 dark:text-amber-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                Checkout cancelled
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                No worries! Your site is saved. You can publish whenever you're ready.
              </p>
            </div>
          </>
        )}
      </div>

      <button
        onClick={onDismiss}
        className={cn(
          "flex-shrink-0 p-1.5 rounded-lg transition-colors",
          isSuccess
            ? "hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
            : "hover:bg-amber-500/20 text-amber-600 dark:text-amber-400"
        )}
        aria-label="Dismiss"
      >
        <svg className="size-5" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}

